import { Camera, CircleStop, History, LogOut, Play, RotateCcw, Volume2, X, createIcons } from 'lucide';
import { cleanAgreementHtml } from './agreementHtml';
import { VerificationApi, ApiError, type Agreement, type MemberProfile, type PersonalRecord } from './api';
import { CameraController } from './camera';
import { LivenessRunner, type LivenessSnapshot } from './liveness';
import { captureFaceFrame } from './faceCapture';
import { CommitmentRecorder } from './commitmentRecorder';
import { SpeechReader } from './speechReader';
import { AudioPrompt } from './audioPrompt';
import { matchReading } from './readingMatch';
import { loadPublicConfig, modelResources, READING_DEFAULTS } from './runtimeConfig';
import { toChineseError } from './errorMessages';
import type { ClientDiagnostics, DemoSession, FlowType } from './types';
import { portalView } from './portalView';
import './styles.css';
import './capture.css';
import './portal.css';

document.body.classList.add('capture-page');
document.querySelector<HTMLDivElement>('#app')!.innerHTML = portalView;
createIcons({ icons: { Camera, CircleStop, History, LogOut, Play, RotateCcw, Volume2, X } });
const el = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
const button = (id: string) => el<HTMLButtonElement>(id);
const dialog = (id: string) => el<HTMLDialogElement>(id);
const labels: Record<FlowType, string> = { FACE_ENROLL: '人脸登记', FACE_VERIFY: '人脸核验', COMMITMENT: '承诺书核验' };
const api = new VerificationApi();
const camera = new CameraController(el<HTMLVideoElement>('camera-video'));
const liveness = new LivenessRunner(el<HTMLVideoElement>('camera-video'));
const recorder = new CommitmentRecorder();
const speech = new SpeechReader();
const audio = new AudioPrompt();
let profile: MemberProfile | null = null;
let session: DemoSession | null = null;
let agreement: Agreement | null = null;
let snapshot: LivenessSnapshot | null = null;
let diagnostics: ClientDiagnostics = { samples: [] };
let generation = 0;
let phase = 'IDLE';
let timer = 0;
let recordedAt = 0;
let previewUrl = '';
let finalText = '';
let recordOffset = 0;
let recordsLoading = false;
let passedReading = false;

function setPhase(value: string, text: string): void {
  phase = value;
  el('camera-stage').dataset.phase = value;
  el('stage-status').textContent = text;
}

function renderProfile(): void {
  const step = profile?.nextStep ?? 'FACE_ENROLL';
  el('member-name').textContent = profile ? `${profile.member.name} · ${profile.member.maskedId}` : '请先登录';
  button('start-button').disabled = !profile;
  button('history-button').disabled = !profile;
  button('logout-button').disabled = !profile;
  button('start-button').textContent = `开始${labels[step]}`;
  el('operation-summary').textContent = profile ? `请按顺序完成${labels[step]}` : '请先登录后继续';
  const steps = Object.keys(labels);
  document.querySelectorAll<HTMLElement>('[data-step]').forEach(node => {
    const index = steps.indexOf(node.dataset.step!);
    node.classList.toggle('active', node.dataset.step === step);
    node.classList.toggle('done', index < steps.indexOf(step));
  });
}

function clearPreview(): void {
  const video = el<HTMLVideoElement>('preview-video');
  video.pause(); video.removeAttribute('src'); video.load(); video.classList.add('hidden');
  if (previewUrl) URL.revokeObjectURL(previewUrl);
  previewUrl = '';
}

/** 作废异步回调后再释放媒体；旧网络响应不得推进下一轮或重新开启摄像头。 */
function reset(cancelRemote = true): void {
  generation++;
  const old = session;
  session = null;
  if (cancelRemote && old) void api.request(`/session/${encodeURIComponent(old.sessionId)}/cancel`, { method: 'POST', keepalive: true }).catch(() => undefined);
  window.clearInterval(timer); timer = 0;
  liveness.stop(); speech.cancel(); recorder.cancel(); camera.stop(); audio.stop(); clearPreview();
  snapshot = null; finalText = ''; passedReading = false;
  el('camera-placeholder').classList.remove('hidden');
  for (const id of ['preview-actions', 'commitment-section']) el(id).classList.add('hidden');
  el('result-panel').classList.remove('visible');
  el('error-box').classList.remove('visible');
  el('error-box').textContent = '';
  setPhase('IDLE', '准备开始'); renderProfile();
}

function fail(error: unknown): void {
  const message = toChineseError(error);
  reset();
  el('error-box').textContent = message;
  el('error-box').classList.add('visible');
  setPhase('ERROR', '请重新操作');
  if (error instanceof ApiError && error.status === 401) {
    profile = null; api.csrfToken = ''; renderProfile();
    if (!dialog('login-dialog').open) dialog('login-dialog').showModal();
  }
}

function result(title: string, copy: string): void {
  el('result-title').textContent = title;
  el('result-copy').textContent = copy;
  el('result-panel').classList.add('visible');
}

async function refreshProfile(): Promise<void> {
  const current = generation;
  const loaded = await api.profile();
  if (current !== generation) return;
  profile = loaded; renderProfile();
}

async function openAgreement(): Promise<void> {
  reset();
  const current = generation;
  button('start-button').disabled = true;
  try {
    // 每次开始都向服务器重新取步骤和协议，不能沿用另一标签页的过期状态。
    await refreshProfile();
    if (current !== generation) return;
    if (!profile!.batchId) {
      const loaded = await api.batch();
      if (current !== generation) return;
      profile = loaded;
    }
    if (current !== generation) return;
    renderProfile();
    const loaded = await api.agreement(profile!.nextStep);
    if (current !== generation) return;
    agreement = loaded;
    el('agreement-title').textContent = loaded.title;
    el('agreement-body').innerHTML = cleanAgreementHtml(loaded.html);
    el<HTMLInputElement>('agreement-check').checked = false;
    button('agreement-continue').disabled = true;
    el('agreement-error').textContent = '';
    dialog('agreement-dialog').showModal();
    el('agreement-body').scrollTop = 0;
  } catch (error) { if (current === generation) fail(error); }
  finally { if (current === generation) button('start-button').disabled = !profile; }
}

async function startStep(): Promise<void> {
  if (!profile?.batchId || !agreement || !el<HTMLInputElement>('agreement-check').checked) return;
  const current = generation;
  const step = profile.nextStep;
  button('agreement-continue').disabled = true;
  audio.unlock();
  dialog('agreement-dialog').close();
  setPhase('REQUESTING_CAMERA', '正在准备摄像头');
  try {
    const challenge = await api.createSession(profile.batchId, step, agreement.version);
    if (current !== generation) {
      void api.request(`/session/${challenge.sessionId}/cancel`, { method: 'POST' }).catch(() => undefined); return;
    }
    session = challenge;
    const config = await loadPublicConfig();
    if (current !== generation) return;
    await camera.start({ withAudio: step === 'COMMITMENT' });
    if (current !== generation) return;
    el('camera-placeholder').classList.add('hidden');
    setPhase('FACE_POSITIONING', '正在加载动作检测');
    await liveness.initialize(modelResources(config));
    if (current !== generation) return;
    diagnostics = { samples: [] };
    const started = performance.now();
    let sampleAt = 0;
    setPhase('LIVENESS_RUNNING', '请按照提示完成动作');
    void audio.play('READY');
    snapshot = await liveness.run(challenge.actions, update => {
      if (current !== generation) return;
      el('action-banner').classList.remove('hidden'); el('action-banner').textContent = update.message;
      el('step-strip').textContent = `动作 ${Math.min(update.currentIndex + 1, update.totalActions)} / ${update.totalActions}`;
      if (performance.now() - sampleAt >= 500 && diagnostics.samples.length < 60) {
        const { faceCount, blinkLeft, blinkRight, yaw, faceSize, centerOffset, qualityPassed } = update.observation;
        diagnostics.samples.push({ elapsedMs: Math.round(performance.now() - started), faceCount, blinkLeft, blinkRight, yaw, faceSize, centerOffset, qualityPassed });
        sampleAt = performance.now();
      }
      if (update.message.includes('回到正面')) void audio.play('RETURN_CENTER');
      else if (update.observation.qualityPassed && update.currentAction) void audio.play(update.currentAction);
    });
    if (current !== generation) return;
    if (step === 'COMMITMENT') {
      setPhase('CAPTURE_READY', '动作完成，请开始朗读');
      el('recording-panel').classList.add('visible'); el('commitment-section').classList.remove('hidden');
      button('record-button').disabled = false; button('stop-record-button').disabled = true;
      el('recording-timer').textContent = '00:00'; el('reading-progress').textContent = '点击上方按钮开始朗读';
      el('random-code').textContent = challenge.randomCode ?? '';
      el('code-label').classList.toggle('hidden', !challenge.randomCode);
      const text = el('commitment-text'); text.replaceChildren();
      for (const char of Array.from(challenge.commitmentText ?? '')) {
        const span = document.createElement('span'); span.className = 'reading-char'; span.textContent = char; text.append(span);
      }
      void audio.play('READ_COMMITMENT', true);
      return;
    }
    setPhase('UPLOADING', '正在核验，请稍候');
    const frame = await captureFaceFrame(el<HTMLVideoElement>('camera-video'), snapshot.observation);
    if (current !== generation) return;
    const response = await api.uploadFace(step === 'FACE_ENROLL' ? 'enroll' : 'verify', challenge.sessionId, snapshot.events, frame.blob, diagnostics);
    if (current !== generation) return;
    session = null; camera.stop(); audio.stop();
    await refreshProfile();
    if (current !== generation) return;
    const passed = step === 'FACE_ENROLL' || response.matched === true;
    setPhase('RESULT', passed ? `${labels[step]}成功` : '人脸核验未通过');
    result(passed ? `${labels[step]}成功` : '人脸核验未通过', passed ? `接下来请完成${labels[profile!.nextStep]}` : '请确认由本人操作，在光线充足的环境重新核验。');
    void audio.play(passed ? 'SUCCESS' : 'RETRY', true);
  } catch (error) { if (current === generation) fail(error); }
}

function updateReading(final: string, partial: string): void {
  finalText = final;
  const match = matchReading(session?.commitmentText ?? '', final, partial);
  const nodes = el('commitment-text').children;
  Array.from(nodes).forEach((node, index) => {
    node.classList.toggle('read-confirmed', match.confirmed.has(index));
    node.classList.toggle('read-tentative', match.tentative.has(index));
  });
  el('reading-progress').textContent = `朗读进度 ${match.confirmed.size + match.tentative.size} / ${match.total}`;
  // 长承诺仅在提词区域内滚动，避免整页移动导致看不到摄像头。
  const last = Math.max(-1, ...match.confirmed, ...match.tentative);
  const active = nodes[last] as HTMLElement | undefined;
  if (active) el('commitment-text').scrollTop = Math.max(0, active.offsetTop - el('commitment-text').offsetTop - 40);
}

async function startReading(): Promise<void> {
  if (phase !== 'CAPTURE_READY' || !session || !snapshot) return;
  const current = generation;
  button('record-button').disabled = true; audio.stop();
  const config = session.configuration ?? READING_DEFAULTS;
  try {
    await speech.start(camera.activeStream, { csrfToken: api.csrfToken, sessionId: session.sessionId, actionEvents: snapshot.events, diagnostics },
      (final, partial) => { if (current === generation) updateReading(final, partial); },
      message => { if (current === generation) fail(new Error(message)); });
    if (current !== generation) return;
    recorder.start(camera.activeStream); recordedAt = performance.now();
    setPhase('RECORDING', '正在朗读核验'); el('recording-state').textContent = '正在朗读';
    timer = window.setInterval(() => {
      const seconds = (performance.now() - recordedAt) / 1000;
      el('recording-timer').textContent = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
      button('stop-record-button').disabled = seconds < config.recordingMinSeconds;
      if (seconds >= config.recordingMaxSeconds) void stopReading();
    }, 200);
  } catch (error) { if (current === generation) fail(error); }
}

async function stopReading(): Promise<void> {
  if (phase !== 'RECORDING' || !session) return;
  const current = generation;
  setPhase('UPLOADING', '正在确认朗读结果'); window.clearInterval(timer); timer = 0;
  button('stop-record-button').disabled = true;
  try {
    const [recording] = await Promise.all([recorder.stop(), speech.finish()]);
    if (current !== generation) return;
    const match = matchReading(session.commitmentText ?? '', finalText);
    // 前端高亮不决定通过；由服务端对自己的最终识别文本与会话话术重新比对。
    passedReading = await speech.report({ clientPassed: match.complete, matchedChars: match.matched, totalChars: match.total, durationSeconds: recording.durationSeconds });
    if (current !== generation) return;
    speech.cancel(); camera.stop(); session = null;
    previewUrl = URL.createObjectURL(recording.blob);
    const video = el<HTMLVideoElement>('preview-video'); video.src = previewUrl; video.classList.remove('hidden');
    await refreshProfile();
    if (current !== generation) return;
    setPhase('PREVIEW', passedReading ? '本轮核验已完成' : '朗读未通过，请重试');
    el('preview-actions').classList.remove('hidden');
    button('rerecord-button').classList.toggle('hidden', passedReading);
    button('finish-button').classList.toggle('hidden', !passedReading);
    result(passedReading ? '三步核验已完成' : '承诺书核验未通过', passedReading ? '您可以查看本轮核验记录。' : '请清晰、完整地朗读全部内容后再结束。');
    el('reading-progress').textContent = passedReading ? '朗读核验通过' : '朗读内容未完全匹配';
    void audio.play(passedReading ? 'SUCCESS' : 'RETRY', true);
  } catch (error) { if (current === generation) fail(error); }
}

async function loadRecords(): Promise<void> {
  if (recordsLoading) return;
  recordsLoading = true; button('records-more').disabled = true;
  const current = generation;
  try {
    const response = await api.request<{ items: PersonalRecord[]; total: number }>(`/myRecords?offset=${recordOffset}&limit=20`);
    if (current !== generation) return;
    for (const record of response.items) {
      const item = document.createElement('article'); item.className = 'personal-record';
      const state = document.createElement('span');
      state.textContent = ({ PASSED: '通过', FAILED: '未通过', PENDING: '待开始', PROCESSING: '处理中', INTERRUPTED: '已中断', EXPIRED: '已过期' } as Record<string,string>)[record.state] ?? '未完成';
      state.classList.toggle('record-pass', record.state === 'PASSED');
      const name = document.createElement('strong'); name.textContent = labels[record.step];
      const details = document.createElement('p'); details.textContent = `${new Date(record.createdAt).toLocaleString('zh-CN')} ${record.reason || ''}`;
      const agreementDetail = document.createElement('details');
      const summary = document.createElement('summary'); summary.textContent = '查看本次同意的协议';
      const content = document.createElement('div');
      agreementDetail.append(summary, content);
      let loaded = false;
      agreementDetail.addEventListener('toggle', () => {
        if (!agreementDetail.open || loaded) return;
        loaded = true; content.textContent = '正在读取';
        void api.request<{ title: string; html: string }>(`/myRecords/${encodeURIComponent(record.sessionId)}`).then(value => {
          const title = document.createElement('strong'); title.textContent = value.title;
          const body = document.createElement('div'); body.innerHTML = cleanAgreementHtml(value.html);
          content.replaceChildren(title, body);
        }).catch(error => { content.textContent = toChineseError(error); loaded = false; });
      });
      item.append(name, state, details, agreementDetail); el('records-list').append(item);
    }
    recordOffset += response.items.length;
    if (recordOffset === 0) el('records-list').textContent = '暂无核验记录';
    button('records-more').classList.toggle('hidden', recordOffset >= response.total);
  } catch (error) { el('records-error').textContent = toChineseError(error); }
  finally { recordsLoading = false; button('records-more').disabled = false; }
}

el('login-form').addEventListener('submit', event => {
  event.preventDefault();
  const name = el<HTMLInputElement>('login-name').value.trim();
  const suffix = el<HTMLInputElement>('login-suffix').value.trim().toUpperCase();
  if (!name || !/^\d{5}[\dX]$/.test(suffix)) { el('login-error').textContent = '请输入姓名及正确的身份证号后六位'; return; }
  button('login-submit').disabled = true; el('login-error').textContent = '';
  void api.login(name, suffix, el<HTMLInputElement>('remember-login').checked).then(value => {
    profile = value; el<HTMLInputElement>('login-suffix').value = ''; dialog('login-dialog').close(); reset(false);
  }).catch(error => { el('login-error').textContent = toChineseError(error); }).finally(() => { button('login-submit').disabled = false; });
});
dialog('login-dialog').addEventListener('cancel', event => event.preventDefault());
button('start-button').addEventListener('click', () => void openAgreement());
el('agreement-check').addEventListener('change', () => { button('agreement-continue').disabled = !el<HTMLInputElement>('agreement-check').checked; });
button('agreement-close').addEventListener('click', () => dialog('agreement-dialog').close());
button('agreement-continue').addEventListener('click', () => void startStep());
button('record-button').addEventListener('click', () => void startReading());
button('stop-record-button').addEventListener('click', () => void stopReading());
button('rerecord-button').addEventListener('click', () => void openAgreement());
button('finish-button').addEventListener('click', () => { reset(false); result('核验已完成', '本轮结果已保存，可在右上角查看记录。'); setPhase('RESULT', '核验已完成'); button('start-button').textContent = '开始新一轮核验'; });
button('reset-button').addEventListener('click', () => { reset(); void refreshProfile().catch(fail); });
button('sound-toggle').addEventListener('click', () => { audio.setEnabled(!audio.isEnabled); button('sound-toggle').setAttribute('aria-pressed', String(audio.isEnabled)); button('sound-toggle').setAttribute('aria-label', audio.isEnabled ? '关闭声音提示' : '开启声音提示'); button('sound-toggle').classList.toggle('is-active', audio.isEnabled); });
button('history-button').addEventListener('click', () => { reset(); el('records-list').replaceChildren(); el('records-error').textContent = ''; recordOffset = 0; dialog('records-dialog').showModal(); void loadRecords(); });
button('records-close').addEventListener('click', () => dialog('records-dialog').close());
button('records-more').addEventListener('click', () => void loadRecords());
button('logout-button').addEventListener('click', () => {
  reset(); button('logout-button').disabled = true;
  void api.request('/auth/logout', { method: 'POST' }).then(() => {
    profile = null; api.csrfToken = ''; renderProfile(); dialog('login-dialog').showModal();
  }).catch(fail).finally(() => { button('logout-button').disabled = !profile; });
});
document.addEventListener('visibilitychange', () => { if (document.hidden && session) { reset(); setPhase('IDLE', '操作已中断，请重新开始'); } });
window.addEventListener('pagehide', () => { reset(); liveness.close(); });
void refreshProfile().catch(error => {
  if (error instanceof ApiError && error.status === 401) { dialog('login-dialog').showModal(); }
  else fail(error);
});
